import { Check, Clear, NavigateNext } from '@mui/icons-material';
import {
    useAcceptContractorInviteMutation,
    useDeclineContractorInviteMutation,
    useGetContractorInvitesQuery,
    useGetIndividualContractorQuery,
} from '../../../../app/Slices/SevenDayApiSlice';
import {
    Alert,
    Box,
    Button,
    ButtonGroup,
    Card,
    List,
    ListItem,
    ListItemSecondaryAction,
    ListItemText,
    Stack,
    Typography,
} from '@mui/material';
import React from 'react';
import toast from '../../../shared/Toast';
import UserRole from '../../../../shared/AuthRoles';
import { useNavigate } from 'react-router';

const PendingInvites = () => {
    const loggedInUser = UserRole();
    const navigate = useNavigate();
    const { data: invites } = useGetContractorInvitesQuery();

    const { data: profile } = useGetIndividualContractorQuery(
        loggedInUser?.company,
        { skip: !loggedInUser?.company },
    );

    const setupComplete = profile?.abn ?? true;

    const [acceptInvite] = useAcceptContractorInviteMutation();
    const [declineInvite] = useDeclineContractorInviteMutation();

    const handleAccept = async (id: number) => {
        try {
            await acceptInvite(id).unwrap();
            toast.success('Invitation accepted');
        } catch (error) {
            toast.error('Error accepting invitation');
        }
    };

    const handleDecline = async (id: number) => {
        try {
            await declineInvite(id).unwrap();
            toast.success('Invitation declined');
        } catch (error) {
            toast.error('Error declining invitation');
        }
    };

    return (
        <Box component='section' aria-labelledby='pending-invites-heading'>
            <Typography
                id='pending-invites-heading'
                variant='subHeading'
                component='h2'
            >
                Pending Invites
            </Typography>
            {invites && invites?.length > 0 && setupComplete ? (
                <List disablePadding>
                    {invites?.map((invite: any) => (
                        <>
                            <ListItem divider disableGutters>
                                <ListItemText
                                    primary={invite.owner.name}
                                    secondary={`Received ${new Date(
                                        invite.sentAt,
                                    ).toLocaleString([], {
                                        day: '2-digit',
                                        month: 'long',
                                        hour12: true,
                                        hour: '2-digit',
                                        minute: '2-digit',
                                    })}`}
                                />

                                <ListItemSecondaryAction>
                                    <ButtonGroup>
                                        <Button
                                            variant='outlined'
                                            startIcon={<Check />}
                                            color='success'
                                            onClick={() =>
                                                handleAccept(invite.id)
                                            }
                                        >
                                            Accept
                                        </Button>
                                        <Button
                                            variant='outlined'
                                            startIcon={<Clear />}
                                            color='error'
                                            onClick={() =>
                                                handleDecline(invite.id)
                                            }
                                        >
                                            Decline
                                        </Button>
                                    </ButtonGroup>
                                </ListItemSecondaryAction>
                            </ListItem>
                        </>
                    ))}
                </List>
            ) : (
                <Stack sx={{ gap: 1, pt: 1 }}>
                    {!setupComplete && (
                        <Alert
                            severity='error'
                            action={
                                <Button
                                    color='inherit'
                                    endIcon={<NavigateNext />}
                                    onClick={() =>
                                        navigate('/contractorProfile')
                                    }
                                >
                                    Complete setup
                                </Button>
                            }
                        >
                            Finish setting up your profile to view your
                            invitations.
                        </Alert>
                    )}
                    <Card variant='outlined'>
                        <Box
                            sx={{
                                p: 4,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            No pending invites.
                        </Box>
                    </Card>
                </Stack>
            )}
        </Box>
    );
};

export default PendingInvites;
